import restClient from "api/restClient";
import { EdgeKind } from "containers/Resources/screens/ResourcesSingle/components/Visualization/DependencyVisualization/dependencyVisualization.service";

/**
 * Shape returned by the backend endpoint
 * `GET /execution/{root_id}/process/relations?ids=<comma-separated ids>`:
 * a map from each supplied descendant execution id to how it relates to the
 * root execution (`parent_child` / `dependency` / `both`). Ids the endpoint
 * could not tag (out of the root's descendant set) are simply absent.
 */
export interface ExecutionRelationsResponse {
    kinds: Record<string, EdgeKind>;
}

/**
 * Fetch the per-row relation kind for the Children-tab "Relation" column.
 *
 * This replaces deriving the kind from the dependency-visualization redux graph,
 * which the backend caps at VISUALIZATION_MAX_NODES (500). By tagging exactly the
 * page's row ids server-side, descendants beyond that cap are still tagged
 * correctly (the bug this fixes: on a >500-descendant execution every paginated
 * Children row rendered "—").
 *
 * Bounded and cheap: the caller passes only the current page's row ids (10–150),
 * not the full descendant set. Returns an empty map for an empty page or a missing
 * root so the column simply renders "—" (fail-soft).
 */
export const fetchExecutionRelationKinds = async (
    rootExecutionId: string,
    ids: string[],
    options: { signal?: AbortSignal } = {}
): Promise<Record<string, EdgeKind>> => {
    if (!rootExecutionId || ids.length === 0) {
        return {};
    }
    const response = await restClient.get(
        `/execution/${encodeURIComponent(rootExecutionId)}/process/relations`,
        {
            // Backend expects a comma-separated string of the page's row ids.
            params: { ids: ids.join(",") },
            signal: options.signal,
        }
    );
    const data = response?.data as ExecutionRelationsResponse | undefined;
    return data?.kinds ?? {};
};
